import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { approveTicket, rejectTicket } from '../services/api/tickets';
import { getApiErrorDetails } from '../utils/api-error';

type TicketBanner = {
  message: string;
  isError: boolean;
};

export const useTicketActions = ({ onSettled }: { onSettled: () => void }) => {
  const queryClient = useQueryClient();
  const [banner, setBanner] = useState<TicketBanner | null>(null);

  const refresh = (ticketId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['tickets-count-draft'] });
    void queryClient.invalidateQueries({ queryKey: ['ticket-runs'] });
    onSettled();
  };

  const fail = (error: unknown) => setBanner({ message: getApiErrorDetails(error), isError: true });

  const approve = useMutation({
    mutationFn: (id: string) => approveTicket(id),
    onSuccess: (result, id) => {
      setBanner({
        message: result.existing
          ? `GitHub-issue finns redan: ${result.issueUrl}`
          : `GitHub-issue skapad: ${result.issueUrl}`,
        isError: false,
      });
      refresh(id);
    },
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectTicket(id, reason),
    onSuccess: (_result, { id }) => {
      setBanner({ message: 'Utkastet avvisades (sparas som dedup-minne).', isError: false });
      refresh(id);
    },
    onError: fail,
  });

  return { banner, approve, reject };
};
